// askSmart-ui/src/pages/TOSP/portal/SalesEntryModal.tsx
import React, { useEffect, useRef, useState } from "react";
import { formatCurrency } from "../format";
import { parseNumeric } from "../validation";
import { daysPastDue, MONTH_NAMES, YearMonth } from "./portalSummary";

interface SalesEntryModalProps {
  /** The month being reported. */
  target: YearMonth;
  /** The day of the following month it's due. */
  dueOn: Date;
  /** Named when the account holds several businesses; empty when it holds one. */
  tenantName?: string;
  onClose: () => void;
  onSubmit: (grossRevenue: number) => void;
}


// One month's Gross Revenue, reported by the tenant.
//
// A modal rather than a screen because it's one field: sending someone to a
// page and back for a single number is more navigation than the task deserves,
// and on a phone the keyboard covers half the screen anyway.
export default function SalesEntryModal({
  target,
  tenantName = "",
  dueOn,
  onClose,
  onSubmit,
}: SalesEntryModalProps) {
  const [amount, setAmount] = useState("");
  const [error, setError] = useState<string | null>(null);
  // A submitted month can't be edited, so the figure is shown back before it's
  // committed. A confirmation that doesn't repeat the number catches nothing —
  // the mistake being guarded against is a mistyped figure, and the tenant
  // can't spot that without seeing it.
  const [confirming, setConfirming] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const monthName = MONTH_NAMES[target.month - 1];

  // Derived from the due date rather than passed in, so a month opened from
  // the sales sheet years after the fact carries the same warning as one the
  // portal opens on arrival. Nothing can show this form for a late month
  // without saying it's late.
  //
  // The same helper the owing queue uses. Two of them measuring days
  // differently is what let a month be queued as late and then shown without
  // the warning that says so.
  const daysLate = daysPastDue(dueOn, new Date());
  const overdue = daysLate > 0;
  const dueToday = daysLate === 0;

  // Straight into the one field there is, so a tenant on a phone gets the
  // keypad without a second tap.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Escape closes, as a dialog should.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const value = parseNumeric(amount);

    if (!amount.trim()) {
      setError("Enter your Gross Revenue for the month.");
      return;
    }
    if (value === null) {
      setError("Gross Revenue must be a number.");
      return;
    }
    // A month with no trading is a real answer, so zero is allowed — but a
    // negative one isn't a figure any lease recognises.
    if (value < 0) {
      setError("Gross Revenue can't be negative.");
      return;
    }

    setConfirming(value);
  };

  return (
    // Clicking away closes; clicking inside doesn't, so a drag that ends
    // outside the panel can't dismiss a part-filled form.
    <div className="tosp-portal-backdrop" onMouseDown={onClose}>
      <div
        className="tosp-portal-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tosp-portal-modal-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="tosp-portal-modal-head">
          <h2 className="tosp-portal-modal-title" id="tosp-portal-modal-title">
            {confirming === null ? "Report" : "Confirm"} {monthName} {target.year} Sales
            {/* Whose figure this is, stated rather than left to whatever is on
                the screen behind. Only an admin can correct a month reported
                against the wrong business. */}
            {tenantName && (
              <span className="tosp-portal-modal-subject"> — {tenantName}</span>
            )}
          </h2>
          <button
            type="button"
            className="tosp-portal-modal-close"
            aria-label="Close"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        {confirming !== null ? (
          <>
            <div className="tosp-portal-modal-body">
              <div className="tosp-portal-confirm-label">
                {monthName} {target.year} Gross Revenue
              </div>
              <div className="tosp-portal-confirm-amount">{formatCurrency(confirming)}</div>

              <div className="tosp-portal-warning" role="alert">
                <strong>This can't be edited once submitted.</strong>
              </div>
            </div>

            <div className="tosp-portal-modal-foot">
              {/* Back rather than Cancel: the figure is kept, so a tenant who
                  spots a typo edits it instead of retyping from nothing. */}
              <button
                type="button"
                className="tosp-portal-btn"
                onClick={() => setConfirming(null)}
              >
                Back
              </button>
              <button
                type="button"
                className="tosp-portal-btn tosp-portal-btn-primary"
                onClick={() => onSubmit(confirming)}
              >
                Confirm and submit
              </button>
            </div>
          </>
        ) : (
        <form onSubmit={submit}>
          {(overdue || dueToday) && (
            <div
              className={`tosp-portal-overdue${dueToday ? " tosp-portal-overdue-today" : ""}`}
              role="alert"
            >
              <span className="tosp-portal-overdue-icon" aria-hidden="true">
                ⚠
              </span>
              <span>
                <strong>
                  {overdue ? "Sales submission overdue" : "Sales submission due today"}
                </strong>
                <span className="tosp-portal-overdue-detail">
                  {monthName} {target.year} {overdue ? "was due" : "is due"}{" "}
                  {MONTH_NAMES[dueOn.getMonth()]} {dueOn.getDate()},{" "}
                  {dueOn.getFullYear()}
                  {overdue && ` — ${daysLate} day${daysLate === 1 ? "" : "s"} ago`}.
                </span>
              </span>
            </div>
          )}

          <div className="tosp-portal-modal-body">
            <label className="tosp-portal-field-label" htmlFor="tosp-portal-gross">
              Gross Revenue
            </label>
            <div className="tosp-portal-money-input">
              <span className="tosp-portal-money-prefix">$</span>
              <input
                id="tosp-portal-gross"
                ref={inputRef}
                className="tosp-portal-input"
                // The decimal keypad on a phone. Not type="number", which
                // brings spinner arrows and swallows a stray comma silently.
                inputMode="decimal"
                autoComplete="off"
                placeholder="0.00"
                value={amount}
                onChange={(event) => {
                  setAmount(event.target.value);
                  setError(null);
                }}
              />
            </div>

            <p className="tosp-portal-field-hint">
              Total Gross Revenue for {monthName} {target.year}, excluding HST. Due{" "}
              {MONTH_NAMES[dueOn.getMonth()]} {dueOn.getDate()}.
            </p>

            {error && (
              <p className="tosp-portal-field-error" role="alert">
                {error}
              </p>
            )}
          </div>

          <div className="tosp-portal-modal-foot">
            <button type="button" className="tosp-portal-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="tosp-portal-btn tosp-portal-btn-primary">
              Submit {monthName}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
}
